import { useTranslation } from "react-i18next";
import { Calendar, Check, FileText, Mail, Pencil, Users } from "lucide-react";
import { Reveal } from "../ui/Reveal";
import { ScribbleUnderline, StickerBadge } from "../ui/decorations";

/** Avatar decorativo do mock (iniciais, sem foto real). */
function MockAvatar({
  initial,
  className,
  size = 22,
}: {
  initial: string;
  className: string;
  size?: number;
}) {
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}
      className={`rounded-full border-2 border-white flex items-center justify-center font-extrabold shrink-0 ${className}`}
    >
      {initial}
    </span>
  );
}

/**
 * Seção de colaboração (destaque da v2): compartilhar o caderno inteiro faz o
 * convidado cair no mesmo quadro Kanban do dono, com acesso de edição e
 * responsável por tarefa — e, nas notas, o convite escolhe entre ver e editar.
 *
 * Fica entre o bento branco e o bloco ink dos Quadros, no creme — mantendo o
 * ritmo branco → creme → ink das seções.
 */
export function CollabShowcase() {
  const { t } = useTranslation("landing");
  return (
    <section id="colaboracao" className="relative py-24 md:py-32 bg-cream overflow-hidden">
      {/* Glow suave atrás do mock */}
      <div className="absolute -right-32 top-1/4 w-[460px] h-[460px] max-w-[90vw] rounded-full bg-primary-400/15 blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-12 items-center">
        {/* Mock do quadro compartilhado (primeiro no desktop, para alternar com
            a seção dos Quadros, que tem o texto à esquerda) */}
        <Reveal delay={120} className="lg:order-2">
          <div className="relative">
            <div className="relative">
              <div className="relative rounded-3xl bg-white border-2 border-ink/10 shadow-xl overflow-hidden rotate-1 transition-transform duration-500 hover:rotate-0">
                {/* Barra do quadro: nome do caderno + pilha de avatares */}
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b-2 border-ink/10 bg-cream/60">
                  <span className="flex items-center gap-2 min-w-0">
                    <Users size={14} className="text-primary-600 shrink-0" />
                    <span className="font-display text-sm font-extrabold text-ink truncate">
                      {t("collab.boardTitle")}
                    </span>
                  </span>
                  <span className="flex items-center -space-x-2 shrink-0">
                    <MockAvatar initial="L" className="bg-primary-500 text-ink" />
                    <MockAvatar initial="M" className="bg-ink text-cream" />
                    <MockAvatar initial="R" className="bg-primary-200 text-ink" />
                    <span
                      aria-hidden="true"
                      className="w-[22px] h-[22px] rounded-full border-2 border-dashed border-ink/25 text-ink/40 text-[11px] font-extrabold flex items-center justify-center bg-white"
                    >
                      +
                    </span>
                  </span>
                </div>

                {/* Três colunas com cards */}
                <div className="grid grid-cols-3 gap-2 p-3" aria-hidden="true">
                  {/* A fazer */}
                  <div className="rounded-xl bg-cream border-2 border-ink/10 p-2">
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-600" />
                      <span className="text-[9px] font-extrabold uppercase tracking-wide text-ink/60">
                        {t("collab.colTodo")}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="rounded-lg bg-white border border-ink/10 px-2 py-2">
                        <p className="text-[10px] font-bold text-ink leading-snug">{t("collab.card1")}</p>
                        <div className="mt-1.5 flex items-center justify-between">
                          <span className="inline-flex items-center gap-1 text-[8px] font-bold text-ink/45">
                            <Calendar size={8} /> {t("collab.cardDate")}
                          </span>
                          <MockAvatar initial="M" size={16} className="bg-ink text-cream" />
                        </div>
                      </div>
                      <div className="rounded-lg bg-white border border-ink/10 px-2 py-2">
                        <p className="text-[10px] font-bold text-ink leading-snug">{t("collab.card2")}</p>
                        <div className="mt-1.5 flex items-center justify-end">
                          <MockAvatar initial="R" size={16} className="bg-primary-200 text-ink" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Fazendo */}
                  <div className="rounded-xl bg-cream border-2 border-ink/10 p-2">
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                      <span className="text-[9px] font-extrabold uppercase tracking-wide text-ink/60">
                        {t("collab.colDoing")}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {/* Card "em movimento", puxado por outra pessoa */}
                      <div className="rounded-lg bg-white border-2 border-primary-400 px-2 py-2 -rotate-2 shadow-lg">
                        <p className="text-[10px] font-bold text-ink leading-snug">{t("collab.card3")}</p>
                        <div className="mt-1.5 flex items-center justify-end">
                          <MockAvatar initial="L" size={16} className="bg-primary-500 text-ink" />
                        </div>
                      </div>
                      <div className="rounded-lg border-2 border-dashed border-ink/15 px-2 py-3" />
                    </div>
                  </div>

                  {/* Feito */}
                  <div className="rounded-xl bg-cream border-2 border-ink/10 p-2">
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-ink/50" />
                      <span className="text-[9px] font-extrabold uppercase tracking-wide text-ink/60">
                        {t("collab.colDone")}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="rounded-lg bg-white border border-ink/10 px-2 py-2">
                        <p className="text-[10px] font-bold text-ink/45 line-through leading-snug">
                          {t("collab.card4")}
                        </p>
                        <div className="mt-1.5 flex items-center justify-between">
                          <Check size={10} strokeWidth={4} className="text-primary-600" />
                          <MockAvatar initial="R" size={16} className="bg-primary-200 text-ink" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sticker: convite por e-mail */}
              <div
                className="absolute -top-4 -left-2 sm:-left-5 animate-float-slow"
                style={{ "--float-rotate": "-3deg" } as React.CSSProperties}
              >
                <StickerBadge className="border-ink bg-primary-500 text-ink shadow-lg" rotate={-4}>
                  <Mail size={11} />
                  {t("collab.stickerInvite")}
                </StickerBadge>
              </div>
              {/* Sticker: responsável */}
              <div
                className="absolute -bottom-5 -right-2 sm:-right-5 animate-float-slower"
                style={{ "--float-rotate": "3deg" } as React.CSSProperties}
              >
                <StickerBadge className="border-ink/15 bg-white text-ink shadow-lg" rotate={3}>
                  <MockAvatar initial="M" size={14} className="bg-ink text-cream" />
                  {t("collab.stickerAssignee")}
                </StickerBadge>
              </div>
            </div>

            {/* Nota compartilhada com permissão de edição */}
            <div className="relative mt-9 ml-4 sm:ml-10 rounded-2xl bg-white border-2 border-ink/10 shadow-lg -rotate-1 transition-transform duration-500 hover:rotate-0 overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b-2 border-ink/10 bg-cream/60">
                <span className="flex items-center gap-1.5 min-w-0">
                  <FileText size={12} className="text-primary-600 shrink-0" />
                  <span className="font-display text-[11px] font-extrabold text-ink truncate">
                    {t("collab.noteTitle")}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1 rounded-md bg-primary-500/20 px-1.5 py-0.5 text-[8px] font-extrabold uppercase tracking-wide text-primary-700 shrink-0">
                  <Pencil size={8} /> {t("collab.noteBadge")}
                </span>
              </div>

              <div className="px-3 py-2.5 space-y-2" aria-hidden="true">
                {/* Linhas do texto da nota, com um trecho "sendo digitado" */}
                <div className="space-y-1.5">
                  <div className="h-1.5 rounded-full bg-ink/10 w-full" />
                  <div className="h-1.5 rounded-full bg-ink/10 w-4/5" />
                  <div className="flex items-center gap-1">
                    <div className="h-1.5 rounded-full bg-primary-400/60 w-1/3" />
                    <span className="w-[2px] h-3 bg-primary-600 animate-pulse rounded-full" />
                  </div>
                </div>

                {/* Quem está dentro e com qual nível de acesso */}
                <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-ink/10">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <MockAvatar initial="M" size={16} className="bg-ink text-cream" />
                    <span className="text-[9px] font-bold text-ink/55 truncate">{t("collab.noteLine1")}</span>
                  </span>
                  <span className="flex items-center gap-1 shrink-0">
                    <span className="rounded-md border border-ink/10 px-1.5 py-0.5 text-[8px] font-bold text-ink/40">
                      {t("collab.notePermView")}
                    </span>
                    <span className="rounded-md border-2 border-ink bg-primary-500 px-1.5 py-0.5 text-[8px] font-extrabold text-ink">
                      {t("collab.notePermEdit")}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Texto */}
        <Reveal className="lg:order-1">
          <StickerBadge className="border-ink/15 bg-white text-ink mb-6" rotate={-2}>
            <Users size={11} className="text-primary-600" />
            {t("collab.badge")}
          </StickerBadge>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold text-ink tracking-tight text-balance leading-[1.1]">
            {t("collab.titlePrefix")}{" "}
            <ScribbleUnderline>{t("collab.titleHighlight")}</ScribbleUnderline>
            {t("collab.titleSuffix")}
          </h2>
          <p className="mt-6 text-ink/70 text-sm sm:text-base leading-relaxed max-w-md">
            {t("collab.subtitle")}
          </p>
          <ul className="mt-8 space-y-3.5">
            {[t("collab.feat1"), t("collab.feat2"), t("collab.feat3"), t("collab.feat4")].map((f) => (
              <li key={f} className="flex items-start gap-2.5">
                <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-primary-500/20 flex items-center justify-center">
                  <Check size={12} className="text-primary-600" strokeWidth={3} />
                </span>
                <span className="text-sm text-ink/75">{f}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs font-semibold text-ink/45 max-w-md leading-relaxed">
            {t("collab.footnote")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
